// Message contracts between the main thread and the network worker.
//
// Discriminated unions on `type` so a single `switch` on either side gets
// exhaustive narrowing from TypeScript. Keep this file free of imports
// from the worker or main code — it must compile as a leaf module so both
// can include it without dragging in DOM- or worker-only globals.

/* eslint-disable @typescript-eslint/no-empty-interface */

// ---------------------------------------------------------------------------
// Main → Worker
// ---------------------------------------------------------------------------

/** Open a WebTransport connection to the given URL. The URL must already
 *  be HTTPS (browser requirement). */
export interface ConnectMsg {
  type: "connect";
  url: string;
}

/** Tear down the active connection, if any. */
export interface DisconnectMsg {
  type: "disconnect";
}

/** Build-mode action this input tick carries. `None` is the wire-light
 *  default; `Place` and `Break` require cursor coordinates to also be
 *  set. Numeric values mirror `shared::protocol::BuildAction`. */
export const BUILD_ACTION_NONE = 0;
export const BUILD_ACTION_PLACE = 1;
export const BUILD_ACTION_BREAK = 2;
export type BuildAction =
  | typeof BUILD_ACTION_NONE
  | typeof BUILD_ACTION_PLACE
  | typeof BUILD_ACTION_BREAK;

/** Encode one input tick and send it as a datagram. `aimAngle` is radians;
 *  `moveX` / `moveY` must each be -1, 0, or +1 (clamped at the wasm side
 *  defensively).
 *
 *  When `buildAction != None`, `buildCells` is the list of cells the
 *  client wants the server to act on this tick — the client tracks
 *  every cell the cursor crossed since the last 30 Hz send, so a fast
 *  sweep doesn't drop placements or breaks on the floor. Empty list
 *  on combat-only ticks. Capped at 16 entries; the input pump
 *  truncates and the wasm encoder enforces it. */
export interface InputMsg {
  type: "input";
  tick: number;
  moveX: -1 | 0 | 1;
  moveY: -1 | 0 | 1;
  aimAngle: number;
  fire: boolean;
  useSkill: boolean;
  buildAction: BuildAction;
  /** Flat `[x0, y0, x1, y1, ...]` pairs. Easier for the wasm boundary
   *  than nested objects and matches how the renderer treats wall
   *  coords. */
  buildCells: Int32Array;
}

export type MainToWorker = ConnectMsg | DisconnectMsg | InputMsg;

// ---------------------------------------------------------------------------
// Worker → Main
// ---------------------------------------------------------------------------

/** Worker has finished loading the wasm module and is ready to accept
 *  connect messages. */
export interface ReadyMsg {
  type: "ready";
  /** Version string from the wasm bundle — useful for client/server
   *  bisects when something desyncs. */
  version: string;
}

/** WebTransport session is open. */
export interface ConnectedMsg {
  type: "connected";
}

/** Connection torn down. `reason` is the human-readable cause; clients
 *  may decide whether to auto-reconnect based on it. */
export interface DisconnectedMsg {
  type: "disconnected";
  reason: string;
}

/** One decoded server snapshot. The five entity arrays are parallel — index
 *  `i` in each refers to the same entity. The two wall arrays are parallel
 *  to each other. Buffer-backed arrays are sent via transferable so no
 *  copy crosses the worker boundary. */
export interface SnapshotMsg {
  type: "snapshot";
  tick: number;
  /** Entity id of the snapshot's recipient — i.e., which row in `ids`
   *  is "me". Set by `Snapshot.your_id` on the server. Required for
   *  prediction (only self is predicted) and camera-follow-self. */
  yourId: number;
  /** `InputCommand.tick` of the last input the server applied for the
   *  recipient. The reconciliation step compares the local predicted
   *  position at this input tick against `entities[yourId].pos`. `0`
   *  means "no input yet processed" — clients should treat strictly. */
  lastInputTick: number;
  /** Current mana of the recipient — feeds the build-mode HUD. */
  yourMana: number;
  /** Match phase: 0=Waiting, 1=Active, 2=Ended. Matches wire
   *  `shared::protocol::MatchPhase`. */
  matchPhase: number;
  /** Server tick the current phase started at. */
  matchSinceTick: number;
  /** Last round's winning PlayerId. 0 = no winner yet. */
  winnerId: number;
  /** Number of entities. Equals `ids.length` and the length of every
   *  parallel entity array. Sent explicitly so the main thread doesn't
   *  have to trust `.length` after transfer. */
  count: number;
  ids: Uint16Array;
  xs: Float32Array;
  ys: Float32Array;
  facings: Float32Array;
  hps: Uint8Array;
  /** Number of walls in this snapshot. Equals `wallXs.length`,
   *  `wallYs.length`, and `wallKinds.length`. */
  wallsCount: number;
  wallXs: Int32Array;
  wallYs: Int32Array;
  /** Per-wall kind tag — 0 = player-placed (destructible), 1 = barrier
   *  (procedural, indestructible). Parallel to `wallXs` / `wallYs`. The
   *  renderer uses this to pick fill/outline color and to decide whether
   *  to merge outlines between adjacent same-kind cells. */
  wallKinds: Uint8Array;
}

/** Non-fatal log line surfaced to the main thread so it can be drawn on
 *  the debug overlay. Worker fatals come through as `disconnected`. */
export interface LogMsg {
  type: "log";
  level: "info" | "warn" | "error";
  message: string;
}

/** Application-level ping result. Round-trip measured against the
 *  server's `connection.rs` text-frame echo, which bypasses the game
 *  loop — so this is pure network RTT (server processes the echo on
 *  the I/O thread, no tick / snapshot quantization). */
export interface PingMsg {
  type: "ping";
  rttMs: number;
}

export type WorkerToMain =
  | ReadyMsg
  | ConnectedMsg
  | DisconnectedMsg
  | SnapshotMsg
  | LogMsg
  | PingMsg;
